'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface CaseStudy {
  id: string;
  tag: string;
  client: string;
  industry: string;
  title: string;
  challenge: string;
  solution: string;
  outcomes: { metric: string; value: string; label: string }[];
  services: string[];
  duration: string;
  teamSize: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 'fortune500-cloud',
    tag: 'IT Staffing',
    client: 'Fortune 500 Financial Services Firm',
    industry: 'Financial Services',
    title: 'Scaling a Cloud Migration Team from 0 to 45 Engineers in 60 Days',
    challenge:
      'A major financial services company had committed to migrating 300+ legacy applications to AWS within 18 months. Their internal team lacked the cloud-native expertise required, and their existing staffing vendors had failed to deliver qualified candidates after 90 days of searching. The project was at risk of missing a critical regulatory deadline.',
    solution:
      'TranceformRIGHT activated its curated talent cloud and deployed a dedicated sourcing team within 24 hours. We pre-screened candidates against a custom technical matrix covering AWS certifications, financial data compliance (SOX, PCI-DSS), and agile delivery experience. A structured onboarding program was co-developed with the client to reduce ramp time from 6 weeks to 12 days.',
    outcomes: [
      { metric: '45', value: '45', label: 'Engineers placed in 60 days' },
      { metric: '94%', value: '94%', label: 'Retention at 12 months' },
      { metric: '3x', value: '3x', label: 'Faster than previous vendor' },
      { metric: '$2.4M', value: '$2.4M', label: 'Saved vs. consulting firm rates' },
    ],
    services: ['IT Staffing', 'Curated Talent Cloud', 'Contract Staffing'],
    duration: '60 days to full deployment',
    teamSize: '45 engineers',
  },
  {
    id: 'healthcare-rpo',
    tag: 'RPO',
    client: 'National Healthcare Network',
    industry: 'Healthcare',
    title: 'Rebuilding a Healthcare IT Department Through Full-Cycle RPO',
    challenge:
      'Following a merger, a national healthcare network needed to consolidate two separate IT departments and hire 120 net-new technology roles within 9 months — all while maintaining HIPAA compliance and integrating two different HR systems. Their internal talent acquisition team was overwhelmed and time-to-hire had ballooned to 87 days.',
    solution:
      "TranceformRIGHT embedded a dedicated RPO team of 6 recruiters directly within the client's HR function. We redesigned the hiring process end-to-end: standardized job descriptions, built a compliance-first screening workflow, and integrated with their ATS. A dedicated healthcare IT talent pipeline was built from scratch using targeted sourcing across 14 channels.",
    outcomes: [
      { metric: '118', value: '118', label: 'Roles filled in 9 months' },
      { metric: '31 days', value: '31 days', label: 'Average time-to-hire' },
      { metric: '100%', value: '100%', label: 'HIPAA compliance maintained' },
      { metric: '64%', value: '64%', label: 'Reduction in time-to-hire' },
    ],
    services: ['Recruitment Process Outsourcing', 'Healthcare Staffing', 'MSP'],
    duration: '9 months',
    teamSize: '118 hires',
  },
  {
    id: 'tech-msp',
    tag: 'MSP / VMS',
    client: 'Global Technology Manufacturer',
    industry: 'Manufacturing & Technology',
    title: 'Centralizing a Fragmented Contingent Workforce Across 12 Vendors',
    challenge:
      'A global technology manufacturer was managing 400+ contingent workers across 12 different staffing vendors with no centralized visibility, inconsistent billing rates, and significant compliance gaps. Procurement estimated they were overpaying by 18–22% due to lack of rate standardization and duplicate vendor markups.',
    solution:
      "TranceformRIGHT implemented a full Managed Service Provider (MSP) program integrated with the client's existing VMS platform. We consolidated vendor relationships, established standardized bill rate cards by skill category, and built a real-time workforce analytics dashboard. A 1099 compliance audit was conducted across all active contractors.",
    outcomes: [
      { metric: '19%', value: '19%', label: 'Reduction in total spend' },
      { metric: '400+', value: '400+', label: 'Workers under single program' },
      { metric: '12→1', value: '12→1', label: 'Vendors consolidated' },
      { metric: '$1.8M', value: '$1.8M', label: 'Annual cost savings' },
    ],
    services: ['MSP', 'VMS Staffing', '1099 Compliance', '1099 Validation'],
    duration: '6 months implementation',
    teamSize: '400+ contingent workers',
  },
  {
    id: 'payroll-compliance',
    tag: 'Global Payroll',
    client: 'Mid-Market SaaS Company',
    industry: 'Software & Technology',
    title: 'Enabling Global Expansion with Compliant Employer of Record Services',
    challenge:
      'A rapidly growing SaaS company needed to hire 60 software engineers across Canada, India, and the UK within 6 months to support a new product line. They had no legal entities in these countries, no payroll infrastructure, and their legal team flagged significant misclassification risk with their existing contractor arrangements.',
    solution:
      "TranceformRIGHT deployed its Employer of Record (EOR) and Global Payroll infrastructure to legally employ workers in all three countries on the client's behalf. We handled entity setup, local labor law compliance, benefits administration, and currency management. A high-hazard payroll protocol was implemented for contractors in regulated roles.",
    outcomes: [
      { metric: '3', value: '3', label: 'Countries operational in 90 days' },
      { metric: '58', value: '58', label: 'Engineers onboarded compliantly' },
      { metric: '0', value: '0', label: 'Compliance violations' },
      { metric: '40%', value: '40%', label: 'Lower cost vs. local entities' },
    ],
    services: ['Employer of Record', 'Global Payroll', 'High Hazard Payroll', '1099 Compliance'],
    duration: '90 days to first hire',
    teamSize: '58 global employees',
  },
  {
    id: 'accounting-finance',
    tag: 'A&F Staffing',
    client: 'Regional Banking Institution',
    industry: 'Banking & Finance',
    title: 'Closing a Critical Accounting & Finance Talent Gap During Audit Season',
    challenge:
      'A regional bank faced an unexpected departure of 8 senior accounting and finance professionals three weeks before their annual external audit. The remaining team lacked capacity to prepare the required documentation, and the bank risked a qualified audit opinion — a serious regulatory and reputational risk.',
    solution:
      "TranceformRIGHT's Accounting & Finance staffing division mobilized within 48 hours, deploying 6 pre-vetted senior accountants and 2 financial analysts with direct banking audit experience. Each candidate had been through our technical assessment covering GAAP, regulatory reporting, and bank-specific reconciliation procedures.",
    outcomes: [
      { metric: '48 hrs', value: '48 hrs', label: 'Time to first deployment' },
      { metric: '8', value: '8', label: 'Specialists placed immediately' },
      { metric: 'Clean', value: 'Clean', label: 'Audit opinion received' },
      { metric: '100%', value: '100%', label: 'Deadline met with zero delays' },
    ],
    services: ['Accounting & Finance Staffing', 'Contract Staffing', 'IT Staffing'],
    duration: '3-week critical engagement',
    teamSize: '8 specialists',
  },
];

export default function CaseStudiesPage() {
  const [activeStudy, setActiveStudy] = useState<string | null>(null);

  const toggleStudy = (id: string) => {
    setActiveStudy(activeStudy === id ? null : id);
  };

  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Hero - Pinnacle1 style */}
      <section
        className="relative overflow-hidden"
        style={{
          paddingTop: '144px',
          paddingBottom: '80px',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 60%, #1F65AB 100%)',
        }}
      >
        <div
          className="absolute right-0 bottom-0 select-none pointer-events-none"
          style={{
            fontSize: '320px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.05)',
            lineHeight: 1,
          }}
        >
          CS
        </div>
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
            >
              Home
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>Case Studies</span>
          </div>
          <div className="max-w-3xl">
            <span
              className="inline-block mb-5 px-4 py-1.5 text-xs font-semibold"
              style={{ background: 'rgba(0,167,255,0.2)', color: '#00A7FF', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Client Success Stories
            </span>
            <h1
              className="mb-4"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: 'clamp(36px, 5vw, 64px)',
                fontWeight: 200,
                color: '#ffffff',
                lineHeight: 1.2,
              }}
            >
              Real Results for Real Clients
            </h1>
            <div style={{ width: '60px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
            <p
              className="mb-8"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
            >
              See how TranceformRIGHT has helped organizations across industries solve their most complex talent and workforce challenges — with measurable, documented outcomes.
            </p>
            <div className="flex flex-wrap gap-8">
              {[
                { value: '1,000+', label: 'Professionals Placed', dark: false },
                { value: '98%', label: 'Client Retention Rate', dark: true },
                { value: '48 hrs', label: 'Average Response Time', dark: false },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '32px',
                      fontWeight: 200,
                      color: '#ffffff',
                      lineHeight: 1,
                    }}
                  >
                    {stat.value}
                  </span>
                  <span
                    style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 400, color: 'rgba(255,255,255,0.6)', marginTop: '4px', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Banner Image - Pinnacle1 style */}
      <section className="relative w-full overflow-hidden" style={{ height: '400px' }}>
        <img
          src="/assets/images/executives-discussion-bg.png"
          alt="Business executives and managers engaged in a strategic discussion around a conference table in a modern corporate office"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'rgba(1,20,45,0.82)' }}></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h6
            className="mb-4"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#00A7FF',
            }}
          >
            Real Results
          </h6>
          <h2
            className="mb-4 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Transforming Challenges into Success Stories
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
          <p
            className="max-w-xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Every engagement is a partnership. Our team works alongside your executives to understand the full scope of your talent challenges and deliver measurable outcomes.
          </p>
        </div>
      </section>

      {/* Case Studies - Pinnacle1 style: white bg, accordion cards */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="space-y-6">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                className="overflow-hidden transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: activeStudy === study.id ? '0 8px 32px rgba(0,145,221,0.12)' : '0 2px 12px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${activeStudy === study.id ? '#00A7FF' : (index % 2 === 0 ? '#00A7FF' : '#01142D')}`,
                }}
              >
                {/* Card Header */}
                <div
                  className="p-8 cursor-pointer group"
                  onClick={() => toggleStudy(study.id)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    <div className="flex items-center gap-4 lg:flex-col lg:items-center lg:w-20 flex-shrink-0">
                      <div
                        className="w-14 h-14 flex items-center justify-center"
                        style={{
                          background: index % 2 === 0 ? '#00A7FF' : '#01142D',
                        }}
                      >
                        <span
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 200, color: '#ffffff' }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <span
                        className="px-3 py-1 text-xs font-semibold"
                        style={{
                          background: index % 2 === 0 ? 'rgba(0,167,255,0.1)' : 'rgba(1,20,45,0.08)',
                          color: index % 2 === 0 ? '#0091DD' : '#01142D',
                          fontFamily: 'Montserrat, sans-serif',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {study.tag}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p
                            className="mb-1"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}
                          >
                            {study.client} · {study.industry}
                          </p>
                          <h2
                            className="mb-3"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '22px', fontWeight: 400, color: 'rgba(0,0,0,0.8)', lineHeight: 1.4 }}
                          >
                            {study.title}
                          </h2>
                          <p
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}
                          >
                            {study.challenge.substring(0, 180)}...
                          </p>
                        </div>
                        <div
                          className="flex-shrink-0 w-10 h-10 flex items-center justify-center transition-all duration-300"
                          style={{
                            background: activeStudy === study.id ? '#00A7FF' : '#FAFAFA',
                            border: '1px solid #e8e8e8',
                            transform: activeStudy === study.id ? 'rotate(180deg)' : 'none',
                          }}
                        >
                          <svg
                            className="w-5 h-5"
                            style={{ color: activeStudy === study.id ? '#ffffff' : '#888888' }}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>

                      {/* Outcome metrics preview */}
                      <div className="flex flex-wrap gap-6 mt-5">
                        {study.outcomes.slice(0, 4).map((outcome, oi) => (
                          <div key={outcome.label} className="flex items-center gap-2">
                            <span
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '20px',
                                fontWeight: 200,
                                color: oi % 2 === 0 ? '#00A7FF' : '#01142D',
                              }}
                            >
                              {outcome.value}
                            </span>
                            <span
                              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#888888', lineHeight: 1.3, maxWidth: '80px' }}
                            >
                              {outcome.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {activeStudy === study.id && (
                  <div
                    className="px-8 pb-8"
                    style={{ borderTop: '1px solid #e8e8e8' }}
                  >
                    <div className="grid lg:grid-cols-2 gap-10 pt-8">
                      <div>
                        <h4
                          className="mb-3"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                        >
                          The Challenge
                        </h4>
                        <div style={{ width: '32px', height: '2px', background: '#00A7FF', marginBottom: '16px' }}></div>
                        <p
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#48494B', lineHeight: 1.7 }}
                        >
                          {study.challenge}
                        </p>
                      </div>
                      <div>
                        <h4
                          className="mb-3"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                        >
                          Our Solution
                        </h4>
                        <div style={{ width: '32px', height: '2px', background: '#01142D', marginBottom: '16px' }}></div>
                        <p
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', color: '#48494B', lineHeight: 1.7 }}
                        >
                          {study.solution}
                        </p>
                      </div>
                    </div>

                    {/* Outcomes Grid */}
                    <div className="mt-8">
                      <h4
                        className="mb-4"
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                      >
                        Outcomes
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {study.outcomes.map((outcome, oi) => (
                          <div
                            key={outcome.label}
                            className="p-5 text-center"
                            style={{ background: oi % 2 === 0 ? '#00A7FF' : '#01142D' }}
                          >
                            <div
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: '28px',
                                fontWeight: 200,
                                color: '#ffffff',
                                lineHeight: 1,
                                marginBottom: '8px',
                              }}
                            >
                              {outcome.value}
                            </div>
                            <div
                              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}
                            >
                              {outcome.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Meta info */}
                    <div
                      className="flex flex-wrap gap-6 mt-6 pt-6"
                      style={{ borderTop: '1px solid #e8e8e8' }}
                    >
                      <div>
                        <span
                          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600, color: '#888888', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}
                        >
                          Duration
                        </span>
                        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B' }}>{study.duration}</span>
                      </div>
                      <div>
                        <span
                          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600, color: '#888888', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}
                        >
                          Team Size
                        </span>
                        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B' }}>{study.teamSize}</span>
                      </div>
                      <div>
                        <span
                          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11px', fontWeight: 600, color: '#888888', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}
                        >
                          Services Used
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {study.services.map((s) => (
                            <span
                              key={s}
                              className="px-2 py-0.5 text-xs"
                              style={{ background: 'rgba(0,167,255,0.1)', color: '#0091DD', fontFamily: 'Poppins, sans-serif' }}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 100%)' }}
      >
        <div className="container-custom text-center">
          <h2
            className="mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Ready to Write Your Success Story?
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 24px' }}></div>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}
          >
            Let’s discuss how TranceformRIGHT can help your organization overcome its talent challenges and achieve measurable results.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300 hover:opacity-90"
            style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
          >
            Contact Us Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
